import React from "react";
import { connect } from "react-redux";
import { filterYearTermsInGroup } from "browse-media/redux/browseMediaHelpers";
import {
  addFacetDialogSelectedPublicationYear,
  addFacetDialogSelectedPublicationYearGroup,
  addFacetDialogExpandedPublicationYearGroup,
  removeFacetDialogSelectedPublicationYear,
  removeFacetDialogSelectedPublicationYearGroup,
  removeFacetDialogExpandedPublicationYearGroup,
} from "browse-media/redux/browseMediaActions";
import { IconButton } from "react-toolbox/lib/button";
import { Checkbox } from "react-toolbox/lib/checkbox";
import IndeterminateCheckboxButton from "components/IndeterminateCheckboxButton";
import styles from "browse-media/components/BrowseMediaFacetPublicationYears.css";
import { any, find, isNil, map, path, pipe, propEq, sum } from "ramda";

const containsGroup = (group, groups) =>
  !isNil(find(propEq("name", group.name), groups));
const containsYearTerm = (yearTerm, yearTerms) =>
  !isNil(find(propEq("label", yearTerm.label), yearTerms));
const getIsSelected = propEq("selected", true);
const sumTermCounts = years => sum(map(path(["yearTerm", "count"]), years));

const getYearsForGroup = (group, selectedYearTerms) =>
  pipe(
    filterYearTermsInGroup(group),
    map(yearTerm => ({
      yearTerm,
      selected: containsYearTerm(yearTerm, selectedYearTerms),
    })),
  );

const getGroupedInfo = (
  availableGroups,
  expandedGroups,
  selectedGroups,
  availableYearTerms,
  selectedYearTerms,
) =>
  map(group => {
    const years = getYearsForGroup(group, selectedYearTerms)(
      availableYearTerms,
    );
    const selected = containsGroup(group, selectedGroups);

    return {
      group: group,
      count: sumTermCounts(years),
      expanded: containsGroup(group, expandedGroups),
      indeterminate: !selected && any(getIsSelected, years),
      selected,
      years: getYearsForGroup(group, selectedYearTerms)(availableYearTerms),
    };
  }, availableGroups);

const BrowseMediaFacetPublicationYears = ({
  groupedInfo,
  onCollapseGroup,
  onExpandGroup,
  onSelectGroup,
  onSelectYearTerm,
}) => (
  <div className={styles.container}>
    <ul className={styles.list}>
      {map(
        groupInfo => (
          <li key={groupInfo.group.name} className={styles.listItem}>
            <div className={styles.groupContainer}>
              {groupInfo.indeterminate ? (
                <div className={styles.indeterminateCheckboxContainer}>
                  <IndeterminateCheckboxButton
                    onClick={() => onSelectGroup(true, groupInfo.group)}
                    className={styles.indeterminateCheckbox}
                  />
                  <span
                    className={styles.indeterminateCheckboxLabel}
                    onClick={() => onSelectGroup(true, groupInfo.group)}
                  >{`${groupInfo.group.name} (${groupInfo.count})`}</span>
                </div>
              ) : (
                <Checkbox
                  theme={styles}
                  checked={groupInfo.selected}
                  label={`${groupInfo.group.name} (${groupInfo.count})`}
                  onChange={selected =>
                    onSelectGroup(selected, groupInfo.group)
                  }
                />
              )}
              <IconButton
                icon={
                  groupInfo.expanded
                    ? "keyboard_arrow_up"
                    : "keyboard_arrow_down"
                }
                onClick={
                  groupInfo.expanded
                    ? () => onCollapseGroup(groupInfo.group)
                    : () => onExpandGroup(groupInfo.group)
                }
              />
            </div>
            {groupInfo.expanded && (
              <ul className={styles.yearList}>
                {map(
                  yearInfo => (
                    <li
                      key={yearInfo.yearTerm.label}
                      className={styles.listItem}
                    >
                      <Checkbox
                        checked={groupInfo.selected || yearInfo.selected}
                        theme={styles}
                        label={`${yearInfo.yearTerm.label} (${
                          yearInfo.yearTerm.count
                        })`}
                        onChange={selected =>
                          onSelectYearTerm(selected, yearInfo.yearTerm)
                        }
                      />
                    </li>
                  ),
                  groupInfo.years,
                )}
              </ul>
            )}
          </li>
        ),
        groupedInfo,
      )}
    </ul>
  </div>
);

const mapStateToProps = state => {
  const { availablePublicationYearGroups } = state.browseMedia;

  const {
    availableTerms,
    selectedTerms,
    expandedPublicationYearGroups,
    selectedPublicationYearGroups,
  } = state.browseMedia.facetDialog;

  const groupedInfo = getGroupedInfo(
    availablePublicationYearGroups,
    expandedPublicationYearGroups,
    selectedPublicationYearGroups,
    availableTerms,
    selectedTerms,
  );

  return {
    groupedInfo,
  };
};

const mapDispatchToProps = dispatch => ({
  onCollapseGroup: group =>
    dispatch(removeFacetDialogExpandedPublicationYearGroup(group)),
  onExpandGroup: group =>
    dispatch(addFacetDialogExpandedPublicationYearGroup(group)),
  onSelectGroup: (selected, group) =>
    selected
      ? dispatch(addFacetDialogSelectedPublicationYearGroup(group))
      : dispatch(removeFacetDialogSelectedPublicationYearGroup(group)),
  onSelectYearTerm: (selected, yearTerm) =>
    selected
      ? dispatch(addFacetDialogSelectedPublicationYear(yearTerm))
      : dispatch(removeFacetDialogSelectedPublicationYear(yearTerm)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  BrowseMediaFacetPublicationYears,
);
